// The sync status and the changes the server refused, each in plain words
// with a one-tap fix where there is a safe one (lib/sync-fixes.ts). Managers
// also see what was refused on other devices (sync_rejections_list).
import type { FailedTx } from '@ims/data'
import { tenantApi, type SyncRejection } from '@ims/remote'
import { Badge, Button, Modal, cx, useToast } from '@ims/ui'
import { RefreshCw } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage, useOnline, useSyncState } from '../hooks.ts'
import { timeAgo } from '../lib/format.ts'
import { orderRow, startOfToday, tablesPath } from '../lib/orders.ts'
import {
  closedOrderId,
  fixKind,
  issueText,
  moveItemsToNewOrder,
  syncErrorText,
  withoutSerials,
  type FixKind,
} from '../lib/sync-fixes.ts'
import { useSession } from '../session.tsx'
import { useCan, useStore, useWorkspace } from '../workspace.tsx'

const FIX_LABEL: Record<FixKind, string> = {
  new_order: 'Move to a new order',
  drop_serials: 'Keep the bill without the serial',
  upgrade: 'See plans',
}

export function SyncPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sync = useSyncState()
  const { engine, repository: repo } = useStore()
  const { current } = useWorkspace()
  const { platform } = useSession()
  const canDiscard = useCan('waiter')
  const isManager = useCan('manager')
  const online = useOnline()
  const navigate = useNavigate()
  const toast = useToast()
  const tenantId = current?.tenant_id ?? null
  const [busy, setBusy] = useState<string | null>(null)
  const [fixes, setFixes] = useState<Record<string, FixKind | null>>({})
  const [others, setOthers] = useState<SyncRejection[] | null>(null)
  const [othersVersion, setOthersVersion] = useState(0)

  const failedKey = sync.failed.map((f) => f.txId).join(',')
  useEffect(() => {
    if (!open) return
    let live = true
    void (async () => {
      const found: Record<string, FixKind | null> = {}
      for (const f of sync.failed) {
        const ops = await engine.failedOps(f.txId)
        let kind = ops ? fixKind(f.code, ops) : null
        if (kind === 'new_order' && ops) {
          // A delivery needs its customer and address again: no one-tap fix.
          const id = closedOrderId(ops)
          const order = id ? await repo.get('orders', id) : null
          if (!order || order.service_type === 'delivery') kind = null
        }
        found[f.txId] = kind
      }
      if (live) setFixes(found)
    })()
    return () => {
      live = false
    }
    // failedKey stands for sync.failed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, failedKey, engine, repo])

  useEffect(() => {
    if (!open || !isManager || !online || !tenantId) return
    let live = true
    tenantApi(platform.supabase)
      .syncRejections(tenantId)
      .then((rows) => live && setOthers(rows))
      .catch(() => live && setOthers(null))
    return () => {
      live = false
    }
  }, [open, isManager, online, tenantId, platform, othersVersion])

  /** Best effort: the managers' list forgets a change this device dealt with. */
  const forget = (txId: string) => {
    if (tenantId)
      void tenantApi(platform.supabase)
        .dismissSyncRejection(tenantId, txId)
        .catch(() => undefined)
  }

  const act = async (id: string, fn: () => Promise<void>) => {
    setBusy(id)
    try {
      await fn()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(null)
    }
  }

  const fix = async (f: FailedTx, kind: FixKind) => {
    if (kind === 'upgrade') {
      onClose()
      navigate('/settings?tab=subscription')
      return
    }
    const ops = await engine.failedOps(f.txId)
    if (!ops || !tenantId) return
    const ctx = { tenantId, now: new Date().toISOString() }
    if (kind === 'drop_serials') {
      await engine.resolve(f.txId, withoutSerials(ops))
      forget(f.txId)
      toast.success('The bill is kept. A manager should check who has that unit.')
      return
    }
    const closedId = closedOrderId(ops)
    const closed = closedId ? await repo.get('orders', closedId) : null
    if (!closed) throw new Error('The order is no longer on this device.')
    const orderNo =
      closed.service_type === 'dine_in' ? null : (await repo.orders.lastOrderNo(startOfToday())) + 1
    const replacement = moveItemsToNewOrder(ctx, ops, orderRow(closed), orderNo)
    await engine.resolve(f.txId, replacement)
    forget(f.txId)
    toast.success('The items are on a new order. Send them to the kitchen from there.')
    onClose()
    const newId = replacement[0]?.row.id
    navigate(newId ? `/tables/${String(newId)}` : tablesPath(closed))
  }

  const mine = new Set(sync.failed.map((f) => f.txId))
  const elsewhere = (others ?? []).filter((r) => !mine.has(r.tx_id))

  return (
    <Modal open={open} onClose={onClose} title="Sync" size="md">
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-background p-3">
            <div className="text-muted">Waiting to sync</div>
            <div className="tabular text-xl font-semibold">{sync.pending}</div>
          </div>
          <div className="rounded-xl bg-background p-3">
            <div className="text-muted">Last synced</div>
            <div className="text-xl font-semibold">{timeAgo(sync.lastPulledAt)}</div>
          </div>
        </div>
        {sync.lastError && <SyncErrorNote error={sync.lastError} />}
        <Button
          icon={<RefreshCw className="size-4" />}
          loading={busy === 'sync'}
          onClick={() => void act('sync', () => engine.syncNow())}
        >
          Sync now
        </Button>
        {sync.failed.length > 0 && (
          <div>
            <h3 className="mb-2 font-semibold">Needs attention</h3>
            <p className="mb-3 text-muted">
              The server refused these changes, so they are on this device only.
            </p>
            <ul className="space-y-2">
              {sync.failed.map((f) => {
                const text = issueText(f.code)
                const kind = fixes[f.txId] ?? null
                return (
                  <li key={f.txId} className="rounded-xl border border-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{text.title}</span>
                      <Badge tone="danger">{f.code}</Badge>
                    </div>
                    <p className="mt-1">{text.explain}</p>
                    <p className="mt-1 text-xs text-muted">
                      {f.message} · {timeAgo(f.failedAt)}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {kind && (
                        <Button
                          size="sm"
                          loading={busy === `f${f.txId}`}
                          onClick={() => void act(`f${f.txId}`, () => fix(f, kind))}
                        >
                          {FIX_LABEL[kind]}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={busy === `r${f.txId}`}
                        onClick={() => void act(`r${f.txId}`, () => engine.retry(f.txId))}
                      >
                        Retry
                      </Button>
                      {canDiscard && (
                        <Button
                          size="sm"
                          variant="danger"
                          loading={busy === `d${f.txId}`}
                          onClick={() =>
                            void act(`d${f.txId}`, async () => {
                              await engine.discard(f.txId)
                              forget(f.txId)
                            })
                          }
                        >
                          Discard
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
        {elsewhere.length > 0 && (
          <div>
            <h3 className="mb-2 font-semibold">Refused on other devices</h3>
            <p className="mb-3 text-muted">
              Fix these on the device that made them; dismiss once they are dealt with.
            </p>
            <ul className="space-y-2">
              {elsewhere.map((r) => (
                <li key={r.tx_id} className="rounded-xl border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{issueText(r.code).title}</span>
                    <Badge tone="warning">{r.code}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    {r.counter_name ? `Counter ${r.counter_name}` : 'A device'}
                    {r.user_email ? ` · ${r.user_email}` : ''} · {timeAgo(r.rejected_at)}
                  </p>
                  <p className="mt-1">{r.message}</p>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    loading={busy === `x${r.tx_id}`}
                    onClick={() =>
                      void act(`x${r.tx_id}`, async () => {
                        if (tenantId)
                          await tenantApi(platform.supabase).dismissSyncRejection(tenantId, r.tx_id)
                        setOthersVersion((v) => v + 1)
                      })
                    }
                  >
                    Dismiss
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  )
}

/** The last sync error in plain words; the technical text is kept as a tooltip for support. */
function SyncErrorNote({ error }: { error: string }) {
  const { tone, text } = syncErrorText(error)
  return (
    <p
      title={error}
      className={cx(
        'rounded-xl p-3',
        tone === 'warning' ? 'bg-warning-light text-warning' : 'bg-danger-light text-danger',
      )}
    >
      {text}
    </p>
  )
}
